import { useMemo, useState } from "react";

import { fetchFilterOptionsConfigs } from "../configs";
import { getFilterType } from "./getFilterType";
import { makeArrayFromLength } from "oolib";
import { useTheme } from "styled-components";
import { useElemInView } from "@/utils/customHooks/useElemInView";

export const useGetOptionsByFilterType = ({
  filterConfig,
  contentTypes,
  activeFilters,
  initEnableDataFetch = false,
  searchTerm,
  disableLimit,
}) => {
  const [enableDataFetch, _setEnableDataFetch] = useState(initEnableDataFetch);
  const theme = useTheme()
  const setEnableDataFetch = () => {
    /**
     * this needs to be toggled to true only once. then we dont want to mess with it, since  
     * we want to only enable the query hook the first time the filter is opened. 
     * After that, we do not want to disable it again
     */
    if(enableDataFetch) return;
    //else enableDataFetch is false so..
    _setEnableDataFetch(true)
  }

  /**
   * this is temporary in order to separate 'tags' from 'contentTags'.
   * AND to merge profileTagType & usersType into a single 'usersType'.
   * Eventually 'tags' & 'profileTagType' will be deprecated,
   * and we wont need this function anymore.
   */
  const { filterType } = getFilterType(filterConfig);
  const useDoNothing = () => ({});

  const { hook, getArgs, convertToOptions, getFilterDisplay, lightboxConfig : _lightboxConfig, getLightboxProps } =
    fetchFilterOptionsConfigs[filterType] || {};

    const useGetFilterOptions = hook || useDoNothing;

  const { data, status, isRefetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useGetFilterOptions(
      ...(getArgs ? getArgs({ 
            filterConfig, 
            enableDataFetch, 
            contentTypes, 
            activeFilters, 
            theme, 
            searchTerm,
            disableLimit,
          }) : [])
    );

  const { observerRef } = useElemInView({
    callback: fetchNextPage,
    enabled: !!hasNextPage && !isFetchingNextPage,
  });

  const lightboxConfig = useMemo(() => {
    // If getLightboxProps is defined, use it to transform API response into lightbox props
    if (_lightboxConfig && getLightboxProps) {
      return {
        ..._lightboxConfig,
        props: {
          ..._lightboxConfig.props,
          ...getLightboxProps({ data, filterConfig, status })
        }
      };
    }

    if(_lightboxConfig) return _lightboxConfig;
    //else
    const getOptions = () => {
      switch (status) {
        case "loading":
          return makeArrayFromLength(8).map((d) => {
            return { loading: true };
          });
        case "error":
          return [{ display: "error loading options", value: "error" }];
        case "success":
            const flattenedData = data?.pages
            ? data?.pages?.reduce((a, b) => [...a, ...(Array.isArray(b) ? b : b?.data)], [])
            : data;
          return convertToOptions({ data: flattenedData || data, filterConfig });
      }
    }

    return ({
      comp: 'MultiSelectFilterTypes',
      props: {
        options: getOptions(),
        searchable: !!searchTerm || filterConfig.searchable,
      }
    })

  }, [status, isRefetching, _lightboxConfig, data, filterConfig, getLightboxProps]);

  return {
    enableDataFetch,
    setEnableDataFetch,
    lightboxConfig,
    status,
    display: getFilterDisplay ? getFilterDisplay({filterConfig}) : filterConfig.filterId,
    observerRef: hasNextPage ? observerRef : null,
    isFetchingNextPage,
    hasNextPage
  };
};
